import type { StorefrontMenuIcon } from '@aeris/contracts'

import type { StorefrontMenuCategory } from './api.ts'

/**
 * O menu de categorias da loja online (F270): os nomes dos ícones na tela e o
 * ponto de partida que vem do site de Ipatinga.
 */

export const ICON_LABELS: Readonly<Record<StorefrontMenuIcon, string>> = {
  smartphone: 'Celular',
  cable: 'Cabo',
  plug: 'Tomada',
  battery: 'Bateria',
  monitor: 'Monitor',
  mouse: 'Mouse',
  keyboard: 'Teclado',
  printer: 'Impressora',
  headphones: 'Fone',
  mic: 'Microfone',
  speaker: 'Caixa de som',
  radio: 'Rádio',
  car: 'Carro',
  home: 'Casa',
  lamp: 'Luminária',
  wrench: 'Ferramenta',
  flashlight: 'Lanterna',
  lock: 'Cadeado',
  gamepad: 'Controle de game',
  tv: 'TV',
  camera: 'Câmera',
  router: 'Roteador',
  book: 'Caderno',
  pencil: 'Lápis',
  backpack: 'Mochila',
  sparkles: 'Beleza',
  blocks: 'Brinquedo',
  watch: 'Relógio',
  phone: 'Telefone',
  cup: 'Copo',
  gift: 'Presente',
  shirt: 'Roupa',
  umbrella: 'Guarda-chuva',
  zap: 'Energia',
  package: 'Caixa',
}

/**
 * O menu do site de Ipatinga, na ordem e com os ícones de lá. É o ponto de
 * partida da loja de GV: as categorias vieram da mesma planilha (F243).
 */
export const IPATINGA_MENU: readonly (readonly [string, StorefrontMenuIcon])[] =
  [
    ['Acessórios de Celular', 'smartphone'],
    ['Cabos e Carregadores', 'cable'],
    ['Informática', 'monitor'],
    ['Fones e Headset', 'headphones'],
    ['Automotivo', 'car'],
    ['Casa e Decoração', 'home'],
    ['Ferramentas e Manutenção', 'wrench'],
    ['Games e Eletrônicos', 'gamepad'],
    ['Papelaria', 'book'],
    ['Bolsas e Mochilas', 'backpack'],
    ['Beleza e Cuidados Pessoais', 'sparkles'],
    ['Brinquedos', 'blocks'],
    ['Relógios e Smartwatch', 'watch'],
    ['Telefonia', 'phone'],
    ['Suportes', 'tv'],
    ['Caixa de Som e Radio', 'speaker'],
    ['Garrafas e Copos', 'cup'],
  ]

const normalized = (text: string): string =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()

/**
 * Aplica o menu de Ipatinga: as categorias dela, na ordem e com o ícone de
 * lá; qualquer outra categoria de cima (as de peça) sai do menu. As
 * subcategorias ficam como estão. Função pura: a tela mostra antes de salvar.
 */
export const withIpatingaMenu = (
  categories: readonly StorefrontMenuCategory[]
): StorefrontMenuCategory[] => {
  const order = new Map(
    IPATINGA_MENU.map(([name, icon], index) => [
      normalized(name),
      { index, icon },
    ])
  )
  return categories
    .map((category) => {
      const known = order.get(normalized(category.name))
      return known === undefined
        ? { ...category, inMenu: false, position: null }
        : {
            ...category,
            inMenu: true,
            position: known.index,
            icon: known.icon,
          }
    })
    .sort(
      (a, b) =>
        (a.position ?? Number.MAX_SAFE_INTEGER) -
          (b.position ?? Number.MAX_SAFE_INTEGER) ||
        a.name.localeCompare(b.name, 'pt-BR')
    )
}
